import React, { useState, useEffect, useCallback, memo } from 'react';
import api from '../services/api';
import './Usuarios.css';

const Usuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [setores, setSetores] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [secretarias, setSecretarias] = useState([]);
  const [subsecretarias, setSubsecretarias] = useState([]);
  const [setorOptions, setSetorOptions] = useState([]);
  const [setorSearch, setSetorSearch] = useState('');
  const [showSetorDropdown, setShowSetorDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    nome_completo: '',
    email: '',
    senha: '',
    telefone: '',
    tipo_usuario: 'servidor',
    id_setor: '',
    id_cidade: '',
    nivel_acesso: 1,
    status_usuario: 'ativo'
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [usuariosRes, setoresRes, cidadesRes, secretariasRes, subsecretariasRes] = await Promise.all([
        api.get('/usuarios'),
        api.get('/setores').catch(() => ({ data: { success: true, data: [] } })),
        api.get('/cidades').catch(() => ({ data: { success: true, data: [] } })),
        api.get('/secretarias?status=ativo').catch(() => ({ data: { success: true, data: [] } })),
        api.get('/subsecretarias?status=ativo').catch(() => ({ data: { success: true, data: [] } }))
      ]);

      if (usuariosRes.data.success) {
        setUsuarios(usuariosRes.data.data);
      }

      if (setoresRes.data.success) {
        setSetores(setoresRes.data.data);
      }

      if (cidadesRes.data.success) {
        setCidades(cidadesRes.data.data);
      }

      let secs = [];
      if (secretariasRes.data.success) {
        secs = secretariasRes.data.data;
        setSecretarias(secs);
      }

      let subs = [];
      if (subsecretariasRes.data.success) {
        subs = subsecretariasRes.data.data;
        setSubsecretarias(subs);
      }
        
      // Criar lista combinada de setores, secretarias e subsecretarias para o autocomplete
        const options = [];
      
      // Adicionar setores tradicionais
      if (setoresRes.data.success && setoresRes.data.data) {
        setoresRes.data.data.forEach(setor => {
          options.push({
            id: setor.id_setor,
            tipo: 'setor',
            sigla: setor.sigla || '',
            descricao: setor.nome_setor || '',
            id_setor: setor.id_setor,
            id_secretaria: null,
            id_subsecretaria: null,
            display: `${setor.sigla || ''} - ${setor.nome_setor || ''} (Setor)`
          });
        });
      }
        
        // Adicionar secretarias
        secs.forEach(sec => {
          options.push({
            id: `secretaria-${sec.id_secretaria}`,
            tipo: 'secretaria',
            sigla: sec.sigla,
            descricao: sec.descricao,
            id_secretaria: sec.id_secretaria,
            id_subsecretaria: null,
            display: `${sec.sigla} - ${sec.descricao} (Secretaria)`
          });
        });
        
        // Adicionar subsecretarias
        subs.forEach(sub => {
          const secretaria = secs.find(s => s.id_secretaria === (sub.id_secretaria || sub.secretaria?.id_secretaria));
          options.push({
            id: `subsecretaria-${sub.id_subsecretaria}`,
            tipo: 'subsecretaria',
            sigla: sub.sigla,
            descricao: sub.descricao,
            id_secretaria: sub.id_secretaria || sub.secretaria?.id_secretaria,
            id_subsecretaria: sub.id_subsecretaria,
            secretariaSigla: secretaria?.sigla || '',
            display: `${sub.sigla} - ${sub.descricao} (Subsecretaria - ${secretaria?.sigla || ''})`
          });
        });
        
      // Ordenar por tipo (setor, secretaria, subsecretaria) e depois por sigla
        options.sort((a, b) => {
        const tipoOrder = { 'setor': 1, 'secretaria': 2, 'subsecretaria': 3 };
        if (tipoOrder[a.tipo] !== tipoOrder[b.tipo]) {
          return tipoOrder[a.tipo] - tipoOrder[b.tipo];
        }
          const siglaA = a.tipo === 'subsecretaria' ? a.secretariaSigla : a.sigla;
          const siglaB = b.tipo === 'subsecretaria' ? b.secretariaSigla : b.sigla;
          if (siglaA !== siglaB) {
            return siglaA.localeCompare(siglaB);
          }
          return a.sigla.localeCompare(b.sigla);
        });
        
        setSetorOptions(options);
    } catch (err) {
      setError('Erro ao carregar dados');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      // Preparar dados para envio
      const dadosEnvio = { ...formData };
      
      // Se não estiver editando ou se a senha foi alterada, incluir senha
      if (!editing || (dadosEnvio.senha && dadosEnvio.senha.trim() !== '')) {
        // Senha será enviada (será hashada no backend)
      } else {
        // Se estiver editando e senha vazia, remover do envio
        delete dadosEnvio.senha;
      }

      // Converter id_cidade para número se existir
      if (dadosEnvio.id_cidade && !isNaN(parseInt(dadosEnvio.id_cidade))) {
        dadosEnvio.id_cidade = parseInt(dadosEnvio.id_cidade);
      } else if (dadosEnvio.id_cidade === '') {
        dadosEnvio.id_cidade = null;
      }

      // id_setor já está no formato correto (string ou número)
      // O backend tratará a conversão

      if (editing) {
        const response = await api.put(`/usuarios/${editing.id_usuario}`, dadosEnvio);
        if (response.data.success) {
          setSuccess('Usuário atualizado com sucesso!');
        } else {
          setError(response.data.message || 'Erro ao atualizar usuário');
          return;
        }
      } else {
        const response = await api.post('/usuarios', dadosEnvio);
        if (response.data.success) {
          setSuccess('Usuário criado com sucesso!');
        } else {
          setError(response.data.message || 'Erro ao criar usuário');
          return;
        }
      }

      setShowModal(false);
      setEditing(null);
      setSetorSearch('');
      setFormData({
        nome_completo: '',
        email: '',
        senha: '',
        telefone: '',
        tipo_usuario: 'servidor',
        id_setor: '',
        id_cidade: '',
        nivel_acesso: 1,
        status_usuario: 'ativo'
      });
      carregarDados();
    } catch (err) {
      console.error('Erro ao salvar usuário:', err);
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors?.[0]?.msg || 
                          'Erro ao salvar usuário';
      setError(errorMessage);
    }
  };

  const handleEdit = useCallback((usuario) => {
    setEditing(usuario);
    
    // Determinar qual setor/secretaria/subsecretaria está selecionado
    let idSetorForm = '';
    let setorSelecionado = null;
    
    // Prioridade: subsecretaria > secretaria > setor
    if (usuario.id_subsecretaria) {
      idSetorForm = `subsecretaria-${usuario.id_subsecretaria}`;
      setorSelecionado = setorOptions.find(opt => 
        opt.tipo === 'subsecretaria' && opt.id_subsecretaria === usuario.id_subsecretaria
      );
    } else if (usuario.id_secretaria) {
      idSetorForm = `secretaria-${usuario.id_secretaria}`;
      setorSelecionado = setorOptions.find(opt => 
        opt.tipo === 'secretaria' && opt.id_secretaria === usuario.id_secretaria
      );
    } else if (usuario.id_setor) {
      idSetorForm = usuario.id_setor.toString();
      // Tentar encontrar setor normal nas opções (se houver)
      setorSelecionado = setorOptions.find(opt => 
        opt.tipo === 'setor' && opt.id_setor === usuario.id_setor
      );
    }
    
    setFormData({
      nome_completo: usuario.nome_completo || '',
      email: usuario.email || '',
      senha: '',
      telefone: usuario.telefone || '',
      tipo_usuario: usuario.tipo_usuario || 'servidor',
      id_setor: idSetorForm,
      id_cidade: usuario.id_cidade || '',
      nivel_acesso: usuario.nivel_acesso || 1,
      status_usuario: usuario.status_usuario || 'ativo'
    });
    
    // Definir o texto de busca baseado no que foi encontrado
    if (setorSelecionado) {
      setSetorSearch(setorSelecionado.display);
    } else if (usuario.subsecretaria) {
      setSetorSearch(`${usuario.subsecretaria.sigla} - ${usuario.subsecretaria.descricao} (Subsecretaria)`);
    } else if (usuario.secretaria) {
      setSetorSearch(`${usuario.secretaria.sigla} - ${usuario.secretaria.descricao} (Secretaria)`);
    } else if (usuario.setor) {
      const sigla = usuario.setor.sigla || '';
      const nome = usuario.setor.nome_setor || '';
      setSetorSearch(sigla ? `${sigla} - ${nome}` : nome || '');
    } else {
      setSetorSearch('');
    }
    
    setShowModal(true);
  }, [setorOptions]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir permanentemente este usuário? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      await api.delete(`/usuarios/${id}`);
      setSuccess('Usuário excluído com sucesso!');
      carregarDados();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao excluir usuário');
    }
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setEditing(null);
    setSetorSearch('');
    setFormData({
      nome_completo: '',
      email: '',
      senha: '',
      telefone: '',
      tipo_usuario: 'servidor',
      id_setor: '',
      id_cidade: '',
      nivel_acesso: 1,
      status_usuario: 'ativo'
    });
  }, []);

  if (loading) {
    return (
      <div className="usuarios-container">
        <div className="loading">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="usuarios">
      <div className="usuarios-header">
        <h1>Usuários</h1>
        <button 
          className="btn-primary"
          onClick={() => setShowModal(true)}
        >
          + Novo Usuário
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="usuarios-table-container">
        <table className="usuarios-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nome</th>
              <th>Email</th>
              <th>Tipo</th>
              <th>Nível</th>
              <th>Status</th>
              <th>Setor</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {usuarios.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-data">
                  Nenhum usuário encontrado
                </td>
              </tr>
            ) : (
              usuarios.map((usuario) => (
                <tr key={usuario.id_usuario}>
                  <td>{usuario.id_usuario}</td>
                  <td>{usuario.nome_completo}</td>
                  <td>{usuario.email}</td>
                  <td>
                    <span className={`badge badge-${usuario.tipo_usuario}`}>
                      {usuario.tipo_usuario}
                    </span>
                  </td>
                  <td>
                    {usuario.nivel_acesso === 1 && 'Colaborador'}
                    {usuario.nivel_acesso === 2 && 'Gestor'}
                    {usuario.nivel_acesso === 3 && 'Administrador'}
                  </td>
                  <td>
                    <span className={`status status-${usuario.status_usuario}`}>
                      {usuario.status_usuario}
                    </span>
                  </td>
                  <td>
                    {usuario.subsecretaria 
                      ? `${usuario.subsecretaria.sigla} - ${usuario.subsecretaria.descricao} (Subsecretaria)`
                      : usuario.secretaria
                      ? `${usuario.secretaria.sigla} - ${usuario.secretaria.descricao} (Secretaria)`
                      : usuario.setor?.nome_setor || '-'}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-edit"
                        onClick={() => handleEdit(usuario)}
                      >
                        Editar
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(usuario.id_usuario)}
                      >
                        Excluir
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? 'Editar Usuário' : 'Novo Usuário'}</h2>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label htmlFor="nome_completo">Nome Completo *</label>
                <input
                  type="text"
                  id="nome_completo"
                  value={formData.nome_completo}
                  onChange={(e) => setFormData({ ...formData, nome_completo: e.target.value })}
                  required
                  maxLength={150}
                  className="form-input"
                  placeholder="Digite o nome completo"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  maxLength={150}
                  className="form-input"
                  placeholder="Digite o email"
                />
              </div>

              <div className="form-group">
                <label htmlFor="senha">
                  Senha {editing ? '(deixe em branco para manter)' : '*'}
                </label>
                <input
                  type="password"
                  id="senha"
                  value={formData.senha}
                  onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                  required={!editing}
                  minLength={6}
                  className="form-input"
                  placeholder="Digite a senha"
                />
              </div>

              <div className="form-group">
                <label htmlFor="telefone">Telefone</label>
                <input
                  type="text"
                  id="telefone"
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                  maxLength={20}
                  className="form-input"
                  placeholder="(00) 00000-0000"
                />
              </div>

              <div className="form-group">
                <label htmlFor="tipo_usuario">Tipo de Usuário *</label>
                <select
                  id="tipo_usuario"
                  value={formData.tipo_usuario}
                  onChange={(e) => setFormData({ ...formData, tipo_usuario: e.target.value })}
                  required
                  className="form-input"
                >
                  <option value="servidor">Servidor</option>
                  <option value="admin">Administrador</option>
                  <option value="protocolo">Protocolo</option>
                  <option value="gestor">Gestor</option>
                  <option value="externo">Externo</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="id_cidade">Cidade</label>
                <select
                  id="id_cidade"
                  value={formData.id_cidade}
                  onChange={(e) => setFormData({ ...formData, id_cidade: e.target.value })}
                  className="form-input"
                >
                  <option value="">Selecione uma cidade</option>
                  {cidades.map((cidade) => (
                    <option key={cidade.id_cidade} value={cidade.id_cidade}>
                      {cidade.sigla} - {cidade.descricao}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="id_setor">Setor (Secretaria/Subsecretaria)</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    id="id_setor"
                    value={setorSearch}
                    onChange={(e) => {
                      const search = e.target.value;
                      setSetorSearch(search);
                      setShowSetorDropdown(true);
                      // Se limpar o campo, limpar também o id_setor
                      if (!search.trim()) {
                        setFormData({ ...formData, id_setor: '' });
                      }
                    }}
                    onFocus={() => setShowSetorDropdown(true)}
                    onBlur={() => {
                      // Delay para permitir clique no dropdown
                      setTimeout(() => setShowSetorDropdown(false), 200);
                    }}
                    className="form-input"
                    placeholder="Digite a sigla (ex: EDU, AED, ADM)"
                    autoComplete="off"
                  />
                  {showSetorDropdown && setorOptions.length > 0 && (
                    <div 
                      className="autocomplete-dropdown"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #E5E7EB',
                        borderRadius: 0,
                        maxHeight: '300px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        marginTop: '4px'
                      }}
                    >
                      {setorOptions
                        .filter(opt => {
                          if (!setorSearch) return true;
                          const search = setorSearch.toUpperCase();
                          return opt.sigla.toUpperCase().includes(search) || 
                                 opt.descricao.toUpperCase().includes(search) ||
                                 opt.display.toUpperCase().includes(search);
                        })
                        .map((opt) => (
                          <div
                            key={opt.id}
                            onClick={() => {
                              setSetorSearch(opt.display);
                              let idSetorValue = '';
                              if (opt.tipo === 'secretaria') {
                                idSetorValue = `secretaria-${opt.id_secretaria}`;
                              } else if (opt.tipo === 'subsecretaria') {
                                idSetorValue = `subsecretaria-${opt.id_subsecretaria}`;
                              } else if (opt.tipo === 'setor') {
                                idSetorValue = opt.id_setor;
                              }
                              setFormData({ 
                                ...formData, 
                                id_setor: idSetorValue
                              });
                              setShowSetorDropdown(false);
                            }}
                            style={{
                              padding: '0.75rem 1rem',
                              cursor: 'pointer',
                              borderBottom: '1px solid #F3F4F6',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#F9FAFB'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                          >
                            <div style={{ fontWeight: 600, color: '#1F2937' }}>
                              {opt.sigla} - {opt.descricao}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.25rem' }}>
                              {opt.tipo === 'secretaria' ? 'Secretaria' : `Subsecretaria (${opt.secretariaSigla})`}
                            </div>
                          </div>
                        ))}
                      {setorOptions.filter(opt => {
                        if (!setorSearch) return false;
                        const search = setorSearch.toUpperCase();
                        return opt.sigla.toUpperCase().includes(search) || 
                               opt.descricao.toUpperCase().includes(search) ||
                               opt.display.toUpperCase().includes(search);
                      }).length === 0 && (
                        <div style={{ padding: '1rem', textAlign: 'center', color: '#9CA3AF' }}>
                          Nenhum resultado encontrado
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {formData.id_setor && (
                  <small style={{ color: '#6B7280', marginTop: '0.25rem', display: 'block' }}>
                    Selecionado: {setorOptions.find(opt => {
                      if (opt.tipo === 'secretaria' && formData.id_setor === `secretaria-${opt.id_secretaria}`) return true;
                      if (opt.tipo === 'subsecretaria' && formData.id_setor === `subsecretaria-${opt.id_subsecretaria}`) return true;
                      if (opt.tipo === 'setor' && formData.id_setor === String(opt.id_setor)) return true;
                      return false;
                    })?.display || formData.id_setor}
                  </small>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="nivel_acesso">Nível de Acesso *</label>
                <select
                  id="nivel_acesso"
                  value={formData.nivel_acesso}
                  onChange={(e) => setFormData({ ...formData, nivel_acesso: parseInt(e.target.value) })}
                  required
                  className="form-input"
                >
                  <option value={1}>1 - Colaborador (Só Leitura)</option>
                  <option value={2}>2 - Gestor (Criar/Editar/Abrir/Fechar)</option>
                  <option value={3}>3 - Administrador (Acesso Total)</option>
                </select>
                <small style={{ color: '#6B7280', marginTop: '0.25rem', display: 'block' }}>
                  {formData.nivel_acesso === 1 && 'Pode apenas visualizar e ler documentos'}
                  {formData.nivel_acesso === 2 && 'Pode criar, editar, abrir, fechar e ler todos os tipos de documentos'}
                  {formData.nivel_acesso === 3 && 'Acesso completo ao sistema, super usuário'}
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="status_usuario">Status *</label>
                <select
                  id="status_usuario"
                  value={formData.status_usuario}
                  onChange={(e) => setFormData({ ...formData, status_usuario: e.target.value })}
                  required
                  className="form-input"
                >
                  <option value="ativo">Ativo</option>
                  <option value="inativo">Inativo</option>
                  <option value="suspenso">Suspenso</option>
                </select>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {editing ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(Usuarios);
